/** @file This file contains parsers for "basic" items. */
import Big from 'big.js';
import _moment from 'moment';
import { identity } from 'purify-ts';
import * as codec from 'purify-ts/Codec';
import { Either, Left, Right } from 'purify-ts/Either';
import { Fraction } from '@codegrade/bigfraction';
import { Maybe, Nothing } from '@codegrade/maybe';
import { NamedUlid, Ulid } from '@codegrade/ulid';
import { AssertionError } from './assert';
import { DateWithoutTime, toMomentMaybe } from './datetime';
import { coerceToError } from './errors';
import { isObject } from './object';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function isEmpty(o) {
    return Object.keys(o).length === 0;
}
/**
 * Report a parse error.
 *
 * @param expectedType The expected type.
 * @param input The given input.
 * @returns A human readable error message.
 */
function reportError(expectedType, input) {
    let receivedString;
    switch (typeof input) {
        case 'undefined':
            receivedString = 'undefined';
            break;
        case 'object':
            if (input == null) {
                receivedString = 'null';
            }
            else if (Array.isArray(input)) {
                receivedString = `an array with value ${JSON.stringify(input)}`;
            }
            else {
                receivedString = `an object with value ${JSON.stringify(input)}`;
            }
            break;
        case 'boolean':
        case 'symbol':
        case 'function':
            receivedString = `a ${typeof input}`;
            break;
        case 'bigint':
            receivedString = `a bigint with value ${input.toString()}`;
            break;
        default:
            receivedString = `a ${typeof input} with value ${JSON.stringify(input)}`;
    }
    return `Expected ${expectedType}, but received ${receivedString}`;
}
/**
 * Parse to a maybe.
 *
 * @param parser The inner parser.
 * @returns A parser that parses `null`s and `undefined`s to a `Nothing`.
 */
export function maybe(parser) {
    return {
        ...codec.Codec.custom({
            decode: input => {
                const asMaybe = Maybe.fromNullable(input);
                if (asMaybe.isJust()) {
                    return parser.decode(asMaybe.value).map(Maybe.of);
                }
                return Right(asMaybe);
            },
            encode: input => input.mapOrDefault(parser.encode, undefined),
            schema: () => {
                const schema = parser.schema();
                return isEmpty(schema)
                    ? []
                    : {
                        oneOf: [schema, { type: 'null' }],
                    };
            },
        }),
        _isOptional: true,
    };
}
/**
 * Parse exact values.
 *
 * @param args The allowed values.
 * @returns A parser that only lets parsing succeed for the given arguments.
 */
export function exactly(...args) {
    const expectedValues = new Set(args);
    let decode;
    // Fast path, if we just have one value just check for equality.
    if (args.length === 1) {
        const expected = args[0];
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        decode = input => input === expected ? Right(expected) : Left(reportError(expected.toString(), input));
    }
    else {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        decode = (input) => expectedValues.has(input)
            ? Right(input)
            : Left(reportError(args.join(', '), input));
    }
    return {
        allValues: expectedValues,
        decode,
        unsafeDecode: input => decode(input).mapLeft(Error).unsafeCoerce(),
        encode: x => x,
        schema: () => ({
            type: typeof args[0],
            enum: Array.from(args),
        }),
    };
}
/**
 * Create a set parser for a parser of an array.
 *
 * @param arrayParser The parser for the array that should become a set.
 * @returns A parser for the array as set. The order of values is, of course, lost after doing this.
 */
export function setFromArray(arrayParser) {
    return codec.Codec.custom({
        decode: inp => arrayParser.decode(inp).map(d => new Set(d)),
        encode: data => arrayParser.encode(Array.from(data)),
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const DEFAULT_BASE_PARSERS = Object.freeze({
    boolean: codec.boolean,
    string: codec.string,
    text: codec.string,
    number: codec.number,
    nullType: codec.nullType,
    maybe,
    interface: codec.Codec.interface,
    array: codec.array,
    strRecord: valueParser => codec.record(codec.string, valueParser),
    exactString: exactly,
    exactBoolean: exactly,
});
const HAS_BUFFER = typeof Buffer !== 'undefined';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var factories;
(function (factories) {
    /**
     * A parser generator for binary data to base64.
     *
     * @param baseParser The base parsers to use.
     * @param useBuffer Should we use nodes Buffer to do the conversion. Defaults to true if it is
     * available.
     * @returns A parser that convers Uint8Arrays to base64 strings.
     */
    factories.base64Parser = (baseParser, useBuffer = HAS_BUFFER) => {
        let makeUint8Array;
        let base64ArrayBuffer;
        if (!useBuffer) {
            const encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            makeUint8Array = str => Uint8Array.from(atob(str), c => c.charCodeAt(0));
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            base64ArrayBuffer = (arrayBuffer) => {
                const base64 = [];
                let bytes;
                // Prevent allocation when the type is already correct.
                if (arrayBuffer instanceof Uint8Array) {
                    bytes = arrayBuffer;
                }
                else {
                    bytes = new Uint8Array(arrayBuffer);
                }
                const byteLength = bytes.byteLength;
                const byteRemainder = byteLength % 3;
                const mainLength = byteLength - byteRemainder;
                let a;
                let b;
                let c;
                let d;
                let chunk;
                // Main loop deals with bytes in chunks of 3
                for (let i = 0; i < mainLength; i += 3) {
                    // Combine the three bytes into a single integer
                    chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
                    // Use bitmasks to extract 6-bit segments from the triplet
                    a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
                    b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
                    c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
                    d = chunk & 63; // 63       = 2^6 - 1
                    // Convert the raw binary segments to the appropriate ASCII encoding
                    base64.push(encodings[a], encodings[b], encodings[c], encodings[d]);
                }
                // Deal with the remaining bytes and padding
                if (byteRemainder === 1) {
                    chunk = bytes[mainLength];
                    a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2
                    // Set the 4 least significant bits to zero
                    b = (chunk & 3) << 4; // 3   = 2^2 - 1
                    base64.push(encodings[a], encodings[b], '==');
                }
                else if (byteRemainder === 2) {
                    chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];
                    a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
                    b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4
                    // Set the 2 least significant bits to zero
                    c = (chunk & 15) << 2; // 15    = 2^4 - 1
                    base64.push(encodings[a], encodings[b], encodings[c], '=');
                }
                return base64.join('');
            };
        }
        else {
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            makeUint8Array = str => Buffer.from(str, 'base64');
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            base64ArrayBuffer = inp => Buffer.from(inp).toString('base64');
        }
        return codec.Codec.custom({
            decode: (input) => baseParser.string.decode(input).map(makeUint8Array),
            encode: val => baseParser.string.encode(base64ArrayBuffer(val)),
        });
    };
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    factories.moment = (baseParsers) => codec.Codec.custom({
        // There is not `altLazy` so doing this with a maybe makes it slower than
        // necessary.
        decode: (val) => baseParsers.string
            .decode(val)
            .mapLeft(err => `Problem with moment: ${err}`)
            .chain(date => toMomentMaybe(date).toEither(`Expected a valid date string, but received a ${date} which cannot be parsed`)),
        encode: val => baseParsers.string.encode(val.clone().utc().toISOString()),
        schema: () => ({ type: 'string', format: 'date-time' }),
    });
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    factories.duration = (baseParsers) => codec.Codec.custom({
        decode: (val) => baseParsers.number
            .decode(val)
            .mapLeft(err => `Problem with duration: ${err}`)
            .map(seconds => _moment.duration(seconds, 'seconds')),
        encode: val => baseParsers.number.encode(val.asSeconds()),
        schema: () => ({ type: 'number', format: 'time-delta' }),
    });
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    factories.numberAsString = (baseParsers) => codec.Codec.custom({
        decode: (value) => baseParsers.string.decode(value).chain(val => {
            if (val === '') {
                return Left('The empty string is not a number parsable string');
            }
            const num = Number(val);
            if (Number.isNaN(num)) {
                return Left(`${val} is not a number parsable string`);
            }
            if (!Number.isFinite(num)) {
                return Left(`${val} is not a finite number`);
            }
            return Right(num);
        }),
        encode: (value) => baseParsers.string.encode(value.toString(10)),
        schema: () => ({
            type: 'string',
            pattern: '^-?\\d+(?:\\.\\d*)?(?:e\\d+)?$',
        }),
    });
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    factories.date = (baseParsers) => {
        const dateRegex = /^(?<year>\d+)-(?<month>\d\d)-(?<day>\d\d)$/;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const padToStr = (val) => {
            if (val <= 0) {
                return '00';
            }
            else if (val < 10) {
                return `0${val.toString(10)}`;
            }
            return val.toString(10);
        };
        return codec.Codec.custom({
            decode: (val) => baseParsers.string
                .decode(val)
                .mapLeft(err => `Problem with date: ${err}`)
                .chain(dateStr => {
                const parsed = dateRegex.exec(dateStr);
                if (parsed == null || parsed.groups == null) {
                    return Left(`Expected a valid date string, but received a ${dateStr} which cannot be parsed`);
                }
                const [year, month, day] = [
                    parsed.groups.year,
                    parsed.groups.month,
                    parsed.groups.day,
                ].map(str => parseInt(str, 10));
                return Right(new DateWithoutTime(year, month, day));
            }),
            encode: val => baseParsers.string.encode(`${val.year}-${padToStr(val.month)}-${padToStr(val.day)}`),
            schema: () => ({ type: 'string', format: 'date' }),
        });
    };
    /**
     * A parser for non empty string.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser for a non empty string.
     */
    factories.nonEmptyString = (baseParsers) => codec.Codec.custom({
        decode: value => baseParsers.string
            .decode(value)
            .chain(val => (val === '' ? Left('Expected non empty string') : Right(val))),
        encode: baseParsers.string.encode.bind(baseParsers.string),
        schema: baseParsers.string.schema.bind(baseParsers.string),
    });
    /**
     * A generator for a parser that strings into decimals.
     *
     * @param baseParsers The base parser to use.
     * @returns A parser that parses strings that look like numbers in to decimals.
     */
    factories.decimal = (baseParsers) => codec.Codec.custom({
        decode: (val) => baseParsers.string
            .decode(val)
            .mapLeft(err => `Problem with decimal: ${err}`)
            .chain(str => /^-?\d+(\.\d+)?$/.test(str)
            ? Right(str)
            : Left(`Decimal has incorrect format: ${str}`))
            .map(str => new Big(str)),
        encode: val => baseParsers.string.encode(val.toString()),
        schema: () => ({ type: 'string', format: 'decimal' }),
    });
    /**
     * A generator for a parser that parses integers.
     *
     * @param baseParsers The base parser to use.
     * @returns A parser that just parses integers.
     */
    factories.integer = (baseParsers) => codec.Codec.custom({
        decode: (inp) => baseParsers.number
            .decode(inp)
            .chain(val => Number.isInteger(val)
            ? Right(val)
            : Left(`Value is not an integer: ${val}`)),
        encode: baseParsers.number.encode.bind(baseParsers.number),
        schema: () => ({
            type: 'integer',
        }),
    });
    /**
     * A parser for a big integer.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser for a big integer.
     */
    function bigInt(baseParsers) {
        const integerLike = /^-?\d+$/;
        return codec.Codec.custom({
            decode: (val) => baseParsers.string.decode(val).chain(res => {
                if (integerLike.test(res)) {
                    return Right(BigInt(res));
                }
                return Left(`The number has incorrect format: ${res}`);
            }),
            encode: (d) => baseParsers.string.encode(d.toString(10)),
            schema: () => ({
                type: 'string',
                pattern: integerLike.toString(),
            }),
        });
    }
    factories.bigInt = bigInt;
    /**
     * A generator for a parser that parses fractions.
     *
     * @param baseParsers The base parser to use.
     * @returns A parser that parses fractions.
     */
    factories.fraction = (baseParsers) => {
        const bigIntParser = bigInt(baseParsers);
        const base = baseParsers.interface({
            n: bigIntParser,
            d: bigIntParser,
        });
        return codec.Codec.custom({
            decode: inp => {
                const asStr = baseParsers.string.decode(inp);
                if (asStr.isRight()) {
                    try {
                        return Right(new Fraction(asStr.extract()));
                    }
                    catch (caught) {
                        return Left(coerceToError(caught).message);
                    }
                }
                return base.decode(inp).chain(obj => {
                    if (obj.d === 0n) {
                        return Left('Cannot construct a fraction with a denominator of 0.');
                    }
                    else {
                        const res = new Fraction(obj.n, obj.d);
                        return Right(res);
                    }
                });
            },
            encode: frac => base.encode({
                n: bigIntParser.encode(frac.s * frac.n),
                d: bigIntParser.encode(frac.d),
            }),
            schema: base.schema.bind(base),
        });
    };
    /**
     * A generator for a parser that parses a set of strings.
     *
     * @param baseParsers The base parser to use.
     * @returns A function that returns a parser that parses a set of strings into a set.
     */
    function set(baseParsers) {
        return (...values) => {
            const inner = codec.array(baseParsers.exactString(...values));
            return setFromArray(inner);
        };
    }
    factories.set = set;
    /**
     * A generator for a parser that parses strings to base64.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser that parses strings to base64 strings.
     */
    function base64String(baseParsers) {
        const base64 = factories.base64Parser(baseParsers);
        return codec.Codec.custom({
            decode: value => base64.decode(value).map(d => new TextDecoder().decode(d)),
            encode: str => base64.encode(new TextEncoder().encode(str)),
        });
    }
    factories.base64String = base64String;
    /**
     * Encode a number as length-prefixed hexadecimal digits.
     *
     * This is useful for ensuring that the encoding of numbers has the same
     * ordering as the numbers themselves.
     *
     * Note: The number MUST NOT be negative and MUST be at most 255 bytes long.
     *
     * @param baseParsers The base parsers to use.
     * @returns A codec between numbers and length-prefixed string
     * representation of the number.
     */
    function lengthPrefixedNumber(baseParsers) {
        const baseParser = baseParsers.string;
        // Encoded values should be at least 2 and at most 256 pairs of
        // hexadecimal characters: 1 for the length prefix and 1 to 255
        // (inclusive) for the actual value.
        const validPattern = '^([0-9a-f]{2}){2,256}$';
        const validRegex = new RegExp(validPattern);
        return codec.Codec.custom({
            encode: (x) => {
                if (x !== Math.floor(x)) {
                    throw new TypeError(`Expected an integer but got: ${x}`);
                }
                if (x < 0 || x > Number.MAX_SAFE_INTEGER) {
                    throw new RangeError(`Number out of range: 0 <= ${x} <= ${Number.MAX_SAFE_INTEGER}`);
                }
                let str = x.toString(16);
                if (str.length % 2 === 1) {
                    str = `0${str}`;
                }
                // We've made sure `str.length` is even already.
                const byteLength = str.length / 2;
                // The range check above should already make sure this is the case,
                // but better safe than sorry.
                AssertionError.assert(byteLength < 256, `${x} appears to consist of more than 255 bytes`);
                return baseParser.encode(`${byteLength.toString(16).padStart(2, '0')}${str}`);
            },
            decode: value => baseParser.decode(value).chain(str => {
                if (!validRegex.test(str)) {
                    return Left(`String should consist of 2 to 256 (inclusive) pairs of hexadecimal digits: ${str}`);
                }
                const byteLength = parseInt(str.slice(0, 2), 16);
                if (str.length !== 2 + 2 * byteLength) {
                    return Left(`String length does not match its prefix: ${str}`);
                }
                // We've already validated that the string contains only hex digits.
                return Right(parseInt(str.slice(2), 16));
            }),
            schema: () => ({
                type: 'string',
                pattern: validPattern,
            }),
        });
    }
    factories.lengthPrefixedNumber = lengthPrefixedNumber;
    /**
     * Returns a parser for a unix timestamp.
     *
     * Note: This parser has seconds precision, values are rounded to the
     * nearest whole second.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser for a unix timestamp.
     */
    function unix(baseParsers) {
        return codec.Codec.custom({
            decode: value => baseParsers.number.decode(value).map(v => _moment.unix(v).utc()),
            encode: value => baseParsers.number.encode(value.unix()),
            schema: () => baseParsers.number.schema(),
        });
    }
    factories.unix = unix;
    /**
     * Returns a parser for a unix timestamp with millisecond precision.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser for a unix timestamp.
     */
    function unixMs(baseParsers) {
        return codec.Codec.custom({
            decode: value => baseParsers.number.decode(value).map(v => _moment(v).utc()),
            encode: value => baseParsers.number.encode(value.valueOf()),
            schema: () => baseParsers.number.schema(),
        });
    }
    factories.unixMs = unixMs;
    /**
     * Returns a parser for a ULID.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser for a ULID.
     */
    function ulid(baseParsers) {
        return codec.Codec.custom({
            decode: inp => baseParsers.string
                .decode(inp)
                .chain(str => Either.encase(() => Ulid.parse(str)).mapLeft(err => err.message)),
            encode: val => baseParsers.string.encode(val.toCanonical()),
            schema: () => ({
                type: 'string',
                pattern: /^[0-7][A-Z0-9]{25}$/,
            }),
        });
    }
    factories.ulid = ulid;
    /**
     * Returns a parser for a named ULID.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser for a ULID.
     */
    function namedUlid(baseParsers) {
        return (name) => codec.Codec.custom({
            decode: inp => baseParsers.string
                .decode(inp)
                .chain(str => Either.encase(() => NamedUlid.parse(name, str)).mapLeft(err => err.message)),
            encode: val => baseParsers.string.encode(val.toCanonical()),
            schema: () => ({
                type: 'string',
                pattern: RegExp(`^${name}_[0-7][A-Z0-9]{25}$`),
            }),
        });
    }
    factories.namedUlid = namedUlid;
    /**
     * Returns a parser for a named ULID.
     *
     * @param baseParsers The base parsers to use.
     * @returns A parser for a ULID.
     */
    function strippedNamedUlid(baseParsers) {
        const inner = ulid(baseParsers);
        const schema = inner.schema.bind(inner);
        return (name) => codec.Codec.custom({
            decode: inp => inner.decode(inp).map(val => val.withName(name)),
            encode: val => inner.encode(val.withoutName()),
            schema,
        });
    }
    factories.strippedNamedUlid = strippedNamedUlid;
})(factories || (factories = {}));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const moment = /* #__PURE__ */ factories.moment(DEFAULT_BASE_PARSERS);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const duration = /* #__PURE__ */ factories.duration(DEFAULT_BASE_PARSERS);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const numberAsString = /* #__PURE__ */ factories.numberAsString(DEFAULT_BASE_PARSERS);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const date = /* #__PURE__ */ factories.date(DEFAULT_BASE_PARSERS);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const base64Parser = /* #__PURE__ */ factories.base64Parser(DEFAULT_BASE_PARSERS);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const nonEmptyString = /* #__PURE__ */ factories.nonEmptyString(DEFAULT_BASE_PARSERS);
/** A codec for decimals, parsed in to instances of {@link Big}. */
export const decimal = /* #__PURE__ */ factories.decimal(DEFAULT_BASE_PARSERS);
/** A parser for integers, so it fails for floating points. */
export const integer = /* #__PURE__ */ factories.integer(DEFAULT_BASE_PARSERS);
/** A parser for bigints. */
export const bigInt = /* #__PURE__ */ factories.bigInt(DEFAULT_BASE_PARSERS);
/** A parser for fractions. */
export const fraction = /* #__PURE__ */ factories.fraction(DEFAULT_BASE_PARSERS);
/** A parser for sets. */
export const set = /* #__PURE__ */ factories.set(DEFAULT_BASE_PARSERS);
/** A parser for base64 strings to strings. */
export const base64String = /* #__PURE__ */ factories.base64String(DEFAULT_BASE_PARSERS);
/** A parser for length-prefixed encoded numbers. */
export const lengthPrefixedNumber = factories.lengthPrefixedNumber(DEFAULT_BASE_PARSERS);
/** A parser for unix timestamps with second precision. */
export const unix = factories.unix(DEFAULT_BASE_PARSERS);
/** A parser for unix timestamps with millisecond precision. */
export const unixMs = factories.unixMs(DEFAULT_BASE_PARSERS);
/** A parser for ulids. */
export const ulid = factories.ulid(DEFAULT_BASE_PARSERS);
/** A parser for named ulids. */
export const namedUlid = factories.namedUlid(DEFAULT_BASE_PARSERS);
/** A parser for named ulids. */
export const strippedNamedUlid = factories.strippedNamedUlid(DEFAULT_BASE_PARSERS);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const uint8Array = codec.Codec.custom({
    decode: (value) => value instanceof Uint8Array ? Right(value) : Left('Value is not a Uint8Array'),
    encode: value => value,
    schema: () => ({}),
});
// This is the same as the `maybe` from purify only it encodes as `null`, not
// `undefined` and it is not seen as optional.
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const maybeNull = (inner) => {
    const caseOf = {
        Just: (x) => inner.decode(x).map(Maybe.of),
        Nothing: () => Right(Nothing),
    };
    return codec.Codec.custom({
        decode: input => Maybe.fromNullable(input).caseOf(caseOf),
        encode: input => input.map(inner.encode).extractNullable(),
        schema: () => {
            const schema = inner.schema();
            return {
                oneOf: isEmpty(schema) ? [] : [schema, { type: 'null' }],
            };
        },
    });
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function always(value) {
    return codec.Codec.custom({
        decode: () => Right(value),
        encode: () => value,
        schema: () => ({}),
    });
}
/**
 * Creates a codec for any JSON object.
 *
 * @param properties The properties to parse.
 * @returns An object parser.
 */
export function object(properties) {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const decode = (input) => {
        if (!isObject(input)) {
            return Left(reportError('an object', input));
        }
        const result = Object.create(null);
        for (const [camel, parser, maybeSnake] of properties) {
            const snake = maybeSnake ?? camel;
            if (!Object.hasOwn(input, snake) &&
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions, no-underscore-dangle
                !parser._isOptional) {
                return Left(`Problem with property "${snake}": it does not exist in received object ${JSON.stringify(input)}`);
            }
            const decodedProperty = parser.decode(input[snake]);
            if (decodedProperty.isLeft()) {
                return Left(`Problem with the value of property "${snake}": ${decodedProperty.extract()}`);
            }
            const value = decodedProperty.extract();
            if (value !== undefined) {
                result[camel] = value;
            }
        }
        return Right(result);
    };
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function encode(input) {
        const result = Object.create(null);
        for (const [camel, parser, maybeSnake] of properties) {
            const snake = maybeSnake ?? camel;
            const val = parser.encode(input[camel]);
            result[snake] = val;
        }
        return result;
    }
    return codec.Codec.custom({
        decode,
        encode,
        schema: () => properties.reduce((acc, [camel, parser, maybeSnake]) => {
            const snake = maybeSnake ?? camel;
            // eslint-disable-next-line no-underscore-dangle
            const isOptional = parser._isOptional;
            if (!isOptional) {
                acc.required.push(snake);
            }
            acc.properties[snake] = parser.schema();
            return acc;
        }, {
            type: 'object',
            properties: {},
            required: [],
        }),
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function taggedUnion(tag, objects, tagSnake, baseParsers = DEFAULT_BASE_PARSERS) {
    let tagParser;
    if (tagSnake === undefined || tag === tagSnake) {
        tagParser = baseParsers.interface({
            [tag]: baseParsers.exactString(...objects.map(a => a[0])),
        });
    }
    else {
        if (baseParsers !== DEFAULT_BASE_PARSERS) {
            throw new Error('Cannot construct given parser, if `tagSnake` is given you cannot pass in custom parsers');
        }
        tagParser = object([
            [tag, baseParsers.exactString(...objects.map(a => a[0])), tagSnake],
        ]);
    }
    const lookup = Object.fromEntries(objects);
    return codec.Codec.custom({
        decode: input => tagParser
            .decode(input)
            .map(res => lookup[res[tag]])
            .chain(parser => parser.decode(input)),
        encode: input => {
            // @ts-expect-error This is too difficult for TS to understand.
            const tagValue = input[tag];
            return lookup[tagValue].encode(input);
        },
        schema: () => ({
            oneOf: objects.map(x => x[1].schema()),
        }),
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class TaggedUnionBuilder {
    tag;
    parsers;
    baseParsers;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(tag, parsers, baseParsers) {
        this.tag = tag;
        this.parsers = parsers;
        this.baseParsers = baseParsers;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static create(tag, baseParsers = DEFAULT_BASE_PARSERS) {
        return new TaggedUnionBuilder(tag, [], baseParsers);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addWithTag(key, parser) {
        const extra = [key, parser];
        return new TaggedUnionBuilder(this.tag, [...this.parsers, extra], this.baseParsers);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    add(key, parser) {
        // @ts-expect-error: Because `key` could be instantiated with a union this isn't valid.
        const record = {
            [this.tag]: this.baseParsers.exactString(key),
        };
        const tagParser = this.baseParsers.interface(record);
        return this.addWithTag(key, codec.intersect(tagParser, parser));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    build() {
        return taggedUnion(this.tag, this.parsers, undefined, this.baseParsers);
    }
}
/*
 * Make a lazy parser that evaulates makeCodec once at the first time it is
 * used.
 */
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function lazy(makeCodec) {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    let getParser = () => {
        const res = makeCodec();
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getParser = () => res;
        // Help the GC cleanup the function.
        // eslint-disable-next-line no-param-reassign
        makeCodec = undefined;
        return res;
    };
    return {
        decode: val => getParser().decode(val),
        encode: val => getParser().encode(val),
        unsafeDecode: input => getParser().unsafeDecode(input),
        schema: () => getParser().schema(),
        // @ts-expect-error _isOptional does not exist on `Codec` but it is used
        // to determine if a prop is optional for records
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        get _isOptional() {
            const parser = getParser();
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions, no-underscore-dangle
            return !!parser._isOptional;
        },
        getParser: () => getParser(),
    };
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function parseJsonResponse(jsonCodec) {
    return async (response) => {
        const json = await response.json();
        const result = jsonCodec.decode(json);
        if (result.isLeft()) {
            throw new Error(`Failed to parse response: ${result.extract()}`);
        }
        return result.unsafeCoerce();
    };
}
/**
 * Get the body of a response as an `ArrayBuffer`.
 *
 * @param response The response to get the body from.
 * @returns The response's body as an `ArrayBuffer`, or an error if one occurred.
 */
export async function parseArrayBufferResponse(response) {
    // Cypress's `Response` class does not have an `arrayBuffer` method, but it does
    // have a `blob` method which resolves to a `Blob` that _does_ implement `arrayBuffer`.
    if (typeof response.arrayBuffer === 'function') {
        return await response.arrayBuffer();
    }
    else {
        const blob = await response.blob();
        return await blob.arrayBuffer();
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const file = codec.unknown;
/**
 * Apply the `base` codec and make its result readonly with `Object.freeze`.
 *
 * @param base The base parser.
 * @returns A new parser that makes the parsed object readonly.
 */
export function readonly(base) {
    return codec.Codec.custom({
        decode: x => base.decode(x).map(y => Object.freeze(y)),
        encode: base.encode,
        schema: base.schema,
    });
}
/**
 * Create a parser that maps an absent value to a default value.
 *
 * @param parser The parser of a maybe value to map.
 * @param makeFallback A function that is called to create the fallback value if the value is
 * absent. This function is also called when encoding, and the value is omitted when (reference)
 * equal to the returned value.
 * @param filterFallback Whether to convert a value equal to the fallback back
 * to `Nothing` or keep it as `Just(fallbackValue)`. If given a function it will be passed the
 * value, and it should return `true` if this value shouls be serialized.
 * @returns A new parser that will use `makeFallback` when the value is absent.
 */
export function withDefault(parser, makeFallback, filterFallback = true) {
    const result = {
        ...codec.Codec.custom({
            decode: (inp) => parser.decode(inp).map(parsed => parsed.orDefaultLazy(makeFallback)),
            encode: (inp) => {
                let value = Maybe.of(inp);
                if (typeof filterFallback === 'boolean') {
                    if (filterFallback) {
                        value = value.filter(x => x !== makeFallback());
                    }
                }
                else {
                    value = value.filter(filterFallback);
                }
                return parser.encode(value);
            },
            schema: parser.schema.bind(parser),
        }),
        // eslint-disable-next-line no-underscore-dangle
        _isOptional: !!parser._isOptional,
    };
    return result;
}
/**
 * Map a parser, applying a function both on decode and on encode.
 *
 * @param parser The parser to map.
 * @param from The function to call before calling the `encode` from `parser`.
 * @param into The function to call after decoding, if it returns a `Left` the entire decoder
 * returns a left.
 * @param includeSchema Should the schema from `parser` be used as the schema.
 * @returns A fresh parser that is mapped by the given functions.
 */
export function mapParser(parser, from, into, includeSchema = false) {
    const result = {
        ...codec.Codec.custom({
            encode: (x) => parser.encode(from(x)),
            decode: x => parser.decode(x).chain(into),
            schema: includeSchema ? undefined : parser.schema.bind(parser),
        }),
        // eslint-disable-next-line no-underscore-dangle
        _isOptional: !!parser._isOptional,
    };
    return result;
}
/**
 * Create a parser with an arbitrary constraint.
 *
 * @param parser The base parser to wrap.
 * @param isOk Check if the parsed value is valid.
 * @param errorMessage The message to display when `isOk` returns false.
 * @returns A fresh parser that only returns `Right`s when `isOk` returns true.
 */
export function withConstraint(parser, isOk, errorMessage) {
    return mapParser(parser, identity, v => (isOk(v) ? Right(v) : Left(errorMessage)), true);
}
/**
 * Add a tag to parsed object.
 *
 * This function is very useful for when you are parsing legacy data that is not a tagged union, but
 * now want to treat it as such for further handling.
 *
 * @param tagName The key that will be used to store the tag under.
 * @param tag The value that should be set as the tag.
 * @param parser The base parser to add the tag to.
 * @returns A parser that will always add `tag` under the key "tag".
 */
export function withTag(tagName, tag, parser) {
    return mapParser(parser, identity, value => Right({ ...value, [tagName]: tag }), true);
}
/**
 * Chain two parsers together.
 *
 * This feeds the decoded output from `a` to `b`, and the reverse is done when encoding.
 *
 * @param a The first parser.
 * @param b The second parsers, receives the decoded output of `a`.
 * @returns A fresh parser constructed as described.
 */
export function chain(a, b) {
    return mapParser(a, (val) => b.encode(val), b.decode.bind(b), false);
}
/**
 * Create a parser from a parser that never fails.
 *
 * @param parser The parser that should be wrapped.
 * @returns A new parser that will never fail parsing, but will return a `Nothing` if it fails
 * instead.
 */
export function nonFailing(parser) {
    return {
        ...codec.Codec.custom({
            encode: value => value.map(v => parser.encode(v)).extract(),
            decode: value => Right(parser.decode(value).caseOf({ Left: Maybe.empty, Right: Maybe.of })),
            schema: () => parser.schema(),
        }),
        _isOptional: true,
    };
}
